import React, { useState, useEffect } from 'react';
import SocialActions from '../../components/SocialActions/SocialActions';

const News = () => {
  const apiURL = process.env.REACT_APP_API_URL;

  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ሁሉም');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [imageDisplayMode, setImageDisplayMode] = useState('contain'); // 'contain' or 'cover'

  const loadPosts = async () => {
    try {
      const response = await fetch(`${apiURL}/news`);
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      // Error loading posts
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, selectedCategory, searchTerm]);

  const filterPosts = () => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory !== 'ሁሉም') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
  };

  const categories = ['ሁሉም', 'ትምህርት', 'በዓላት'];

  const featuredPosts = posts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('am-ET', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="news-container max-w-7xl mx-auto p-6">
      {/* Enhanced Page Header */}
      <div className="page-header text-center mb-4">
        <h1 className="page-main-title">
          📰 ዜናዎች እና መረጃዎች
        </h1>
      </div>

      {/* Enhanced Search and Filter */}
      <div className="controls-container">
        <div className="controls-grid">
          <div className="control-group">
            <label className="control-label">🔍 ፈልግ</label>
            <input type="text" placeholder="በርዕስ ወይም ይዘት ይፈልጉ..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="control-input" />
          </div>
          <div className="control-group">
            <label className="control-label">🏷️ ምድብ</label>
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="control-select">
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div className="control-group">
            <label className="control-label">🖼️ ምስል</label>
            <div className="flex gap-1.5">
              <button onClick={() => setImageDisplayMode('contain')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${imageDisplayMode === 'contain' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                ሙሉ
              </button>
              <button onClick={() => setImageDisplayMode('cover')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${imageDisplayMode === 'cover' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                ቁረጥ
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Featured Posts */}
      {featuredPosts.length > 0 && selectedCategory === 'ሁሉም' && !searchTerm && (
        <div className="featured-section mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <span className="text-4xl">⭐</span>
            ዋና ዜናዎች
          </h2>
          <div className="featured-posts grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredPosts.map(post => (
              <div
                key={post.id}
                className="featured-post bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => setSelectedPost(post)}
              >
                {post.image && (
                  <div className="featured-post-image h-48 overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className={`w-full h-full ${imageDisplayMode === 'contain' ? 'object-contain' : 'object-cover'} hover:scale-105 transition-transform duration-300`}
                    />
                  </div>
                )}
                <div className="featured-post-content p-6">
                  <span className="post-category-badge inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {post.category}
                  </span>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">{post.title}</h3>
                  <div 
                    className="text-gray-600 mb-4 line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: post.content.length > 150 
                        ? post.content.substring(0, 150) + '...'
                        : post.content
                    }}
                  />
                  <div className="post-meta flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <span>👤</span>
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <span>📅</span>
                      {formatDate(post.date)}
                    </span>
                  </div>
                  
                  {/* Social Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <SocialActions
                      contentId={post.id}
                      contentType="news"
                      title={post.title}
                      content={post.content}
                      showBookmark={true}
                      showShare={true}
                      showLike={true}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Regular Posts */}
      <div className="posts-section">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <span className="text-4xl">📋</span>
          ሁሉም ልጥፎች ({regularPosts.length})
        </h2>
        {regularPosts.length === 0 ? (
          <div className="no-posts text-center py-12 bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-400 text-2xl">😔</span>
            </div>
            <p className="text-gray-500 text-lg mb-2">ምንም ልጥፍ አልተገኘም</p>
            {searchTerm && (
              <p className="text-gray-400 text-sm">የፍለጋ ቃልዎን ይቀይሩ እና እንደገና ይሞክሩ</p>
            )}
          </div>
        ) : (
          <div className="posts-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularPosts.map(post => (
              <div
                key={post.id}
                className="post-card bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => setSelectedPost(post)}
              >
                {post.image && (
                  <div className="post-card-image h-40 overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className={`w-full h-full ${imageDisplayMode === 'contain' ? 'object-contain' : 'object-cover'} hover:scale-105 transition-transform duration-300`}
                    />
                  </div>
                )}
                <div className="post-card-content p-6">
                  <span className="post-category-badge inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2">{post.title}</h3>
                  <div 
                    className="text-gray-600 mb-4 line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: post.content.length > 120 
                        ? post.content.substring(0, 120) + '...'
                        : post.content
                    }}
                  />
                  <div className="post-meta flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <span>👤</span>
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <span>📅</span>
                      {formatDate(post.date)}
                    </span>
                  </div>
                  
                  {/* Social Actions */}
                  <div className="mb-4">
                    <SocialActions
                      contentId={post.id}
                      contentType="news"
                      title={post.title}
                      content={post.content}
                      showBookmark={true}
                      showShare={true}
                      showLike={true}
                      className="scale-90"
                    />
                  </div>
                  
                  <button className="read-more-btn w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-medium">
                    ተጨማሪ ያንብቡ →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Post Modal */}
      {selectedPost && (
        <div className="post-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedPost(null)}>
          <div className="post-modal-content bg-white rounded-xl shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header flex justify-between items-center p-6 border-b border-gray-200">
              <span className="post-category-badge bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                {selectedPost.category}
              </span>
              <button 
                className="modal-close w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200" 
                onClick={() => setSelectedPost(null)}
              >
                ✕
              </button>
            </div>
            
            {selectedPost.image && (
              <div className="modal-image h-64 overflow-hidden bg-gray-100 flex items-center justify-center">
                <img 
                  src={selectedPost.image} 
                  alt={selectedPost.title} 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            <div className="modal-body p-6 overflow-y-auto max-h-96">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">{selectedPost.title}</h2>
              <div className="post-meta flex items-center gap-6 text-gray-600 mb-6 pb-4 border-b border-gray-200">
                <span className="flex items-center gap-2">
                  <span className="text-lg">👤</span>
                  <span className="font-medium">{selectedPost.author}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-lg">📅</span>
                  <span>{formatDate(selectedPost.date)}</span>
                </span>
              </div>
              <div className="modal-content prose prose-lg max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed text-justify"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
